import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../Config';
import '../css/style/schedule.css';

const getAuth = () => {
    const stored = localStorage.getItem('user');
    if (!stored) return null;
    return JSON.parse(stored);
};

// 파일 → base64 data URI
const readAsDataURL = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

// 단계별 색상 팔레트 (phase 순서대로 순환)
const PHASE_COLORS = ['#4f8cff', '#34c759', '#ff9f0a', '#ff375f', '#af52de', '#5ac8fa', '#ffcc00', '#8e8e93'];

const DAY_MS = 86400000;
const toDate = (s) => new Date(`${s}T00:00:00`);
const isValidDate = (d) => d instanceof Date && !isNaN(d);
const fmt = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

const Schedule = () => {
    const navigate = useNavigate();
    const user = getAuth();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [scheduleText, setScheduleText] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);     // { projectName, tasks } — 편집 대상
    const [currentId, setCurrentId] = useState(null); // 저장된 공정표 ID (없으면 신규)
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState('');
    const [savedList, setSavedList] = useState([]);

    const isPaid = user?.role === 'Y' || user?.role === 'R';
    const authHeader = useCallback(
        () => (user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        [user?.token]
    );

    // 저장된 공정표 목록 불러오기
    const loadList = useCallback(async () => {
        if (!user?.token) return;
        try {
            const res = await fetch(`${BASE_URL}/schedule/list`, { headers: authHeader() });
            if (res.ok) setSavedList(await res.json());
        } catch { /* noop */ }
    }, [user?.token, authHeader]);

    useEffect(() => { loadList(); }, [loadList]);

    // 간트차트 좌표 계산 (훅은 early return 위에 위치해야 함)
    const gantt = useMemo(() => {
        if (!result?.tasks?.length) return null;
        const tasks = result.tasks;
        const valid = tasks.filter(t => isValidDate(toDate(t.startDate)) && isValidDate(toDate(t.endDate)));
        if (!valid.length) return null;

        const min = Math.min(...valid.map(t => toDate(t.startDate).getTime()));
        const max = Math.max(...valid.map(t => toDate(t.endDate).getTime()));
        const totalDays = Math.max(1, Math.round((max - min) / DAY_MS) + 1);

        const phases = [...new Set(tasks.map(t => t.phase))];
        const colorOf = (phase) => PHASE_COLORS[Math.max(0, phases.indexOf(phase)) % PHASE_COLORS.length];

        const rows = tasks.map(t => {
            const sd = toDate(t.startDate), ed = toDate(t.endDate);
            if (!isValidDate(sd) || !isValidDate(ed)) return { ...t, left: 0, width: 0, color: colorOf(t.phase), invalid: true };
            const s = sd.getTime(), e = ed.getTime();
            const left = ((s - min) / DAY_MS / totalDays) * 100;
            const days = Math.max(1, Math.round((e - s) / DAY_MS) + 1);
            const width = (days / totalDays) * 100;
            return { ...t, left, width, color: colorOf(t.phase) };
        });

        return { rows, phases, colorOf, min: new Date(min), max: new Date(max), totalDays };
    }, [result]);

    // ── 로그인/권한 가드 ──────────────────────────────
    if (!user?.token) {
        return (
            <div className="schedule-guard">
                <p>로그인이 필요합니다.</p>
                <button onClick={() => navigate('/login')}>로그인하기</button>
            </div>
        );
    }
    if (!isPaid) {
        return (
            <div className="schedule-guard">
                <p>견적서 일정표 기능은 구독 회원 전용입니다.</p>
                <button onClick={() => navigate('/payment')}>구독하기</button>
            </div>
        );
    }

    const detectFileType = (f) => {
        if (f.type === 'application/pdf') return 'pdf';
        if (f.type.startsWith('image/')) return 'image';
        return null;
    };

    const handleFile = (f) => {
        if (!f) return;
        const t = detectFileType(f);
        if (!t) { setError('PDF 또는 이미지 파일만 업로드할 수 있습니다.'); return; }
        if (f.size > 11 * 1024 * 1024) { setError('파일 용량은 11MB 이하만 가능합니다.'); return; }
        setError('');
        setFile(f);
    };

    const handleGenerate = async () => {
        if (loading) return;
        if (!file) { setError('견적서 파일을 업로드해주세요.'); return; }
        if (!scheduleText.trim()) { setError('공사일정 요구사항을 입력해주세요.'); return; }

        setError('');
        setSavedMsg('');
        setLoading(true);
        setResult(null);
        setCurrentId(null);

        try {
            const dataUrl = await readAsDataURL(file);
            const res = await fetch(`${BASE_URL}/schedule`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({
                    file: dataUrl,
                    fileType: detectFileType(file),
                    scheduleText: scheduleText.trim(),
                }),
            });
            const data = await res.json();
            if (!res.ok) setError(data.error || '일정표 생성에 실패했습니다.');
            else setResult(data.schedule);
        } catch {
            setError('요청 중 오류가 발생했습니다. 다시 시도해주세요.');
        } finally {
            setLoading(false);
        }
    };

    // ── 편집 핸들러 ──────────────────────────────
    const updateProject = (name) => setResult(prev => ({ ...prev, projectName: name }));

    const updateTask = (idx, field, value) => {
        setResult(prev => {
            const tasks = prev.tasks.map((t, i) => (i === idx ? { ...t, [field]: value } : t));
            // 날짜를 바꾸면 기간 자동 재계산
            if (field === 'startDate' || field === 'endDate') {
                const t = tasks[idx];
                const s = toDate(t.startDate), e = toDate(t.endDate);
                if (isValidDate(s) && isValidDate(e) && e >= s) {
                    tasks[idx] = { ...t, durationDays: Math.round((e - s) / DAY_MS) + 1 };
                }
            }
            return { ...prev, tasks };
        });
        setSavedMsg('');
    };

    const updateDeps = (idx, text) => {
        const deps = text.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
        updateTask(idx, 'dependsOn', deps);
    };

    const addTask = () => {
        setResult(prev => ({
            ...prev,
            tasks: [...(prev?.tasks || []), {
                id: `task-${Date.now()}`,
                name: '새 작업',
                phase: '',
                startDate: todayStr(),
                endDate: todayStr(),
                durationDays: 1,
                dependsOn: [],
            }],
        }));
        setSavedMsg('');
    };

    const removeTask = (idx) => {
        setResult(prev => {
            const removedId = prev.tasks[idx]?.id;
            const tasks = prev.tasks
                .filter((_, i) => i !== idx)
                .map(t => ({ ...t, dependsOn: (t.dependsOn || []).filter(d => d !== removedId) }));
            return { ...prev, tasks };
        });
        setSavedMsg('');
    };

    // ── 저장 / 불러오기 / 삭제 ──────────────────────────────
    const saveSchedule = async () => {
        if (saving || !result) return;
        setSaving(true);
        setSavedMsg('');
        try {
            const res = await fetch(`${BASE_URL}/schedule/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ id: currentId, projectName: result.projectName, tasks: result.tasks }),
            });
            const data = await res.json();
            if (!res.ok) { setError(data.error || '저장에 실패했습니다.'); }
            else {
                setCurrentId(data.id);
                setSavedMsg('저장되었습니다.');
                loadList();
            }
        } catch {
            setError('저장 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const loadSchedule = async (id) => {
        try {
            const res = await fetch(`${BASE_URL}/schedule/${id}`, { headers: authHeader() });
            const data = await res.json();
            if (!res.ok) { setError(data.error || '불러오기에 실패했습니다.'); return; }
            setResult({ projectName: data.projectName, tasks: data.tasks });
            setCurrentId(data.id);
            setError('');
            setSavedMsg('');
        } catch {
            setError('불러오기 중 오류가 발생했습니다.');
        }
    };

    const deleteSchedule = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm('이 공정표를 삭제할까요?')) return;
        try {
            const res = await fetch(`${BASE_URL}/schedule/${id}`, { method: 'DELETE', headers: authHeader() });
            if (res.ok) {
                if (currentId === id) { setResult(null); setCurrentId(null); }
                loadList();
            }
        } catch { /* noop */ }
    };

    return (
        <div className="schedule-page">
            <header className="schedule-header">
                <button className="schedule-back" onClick={() => navigate('/')}>← 홈</button>
                <h1>견적서 기반 공정표 생성</h1>
            </header>

            {/* 저장된 공정표 목록 */}
            {savedList.length > 0 && (
                <div className="schedule-saved">
                    <h3>내 공정표</h3>
                    <ul>
                        {savedList.map(s => (
                            <li key={s.ID} className={currentId === s.ID ? 'active' : ''} onClick={() => loadSchedule(s.ID)}>
                                <span className="saved-name">{s.PROJECT_NAME}</span>
                                <span className="saved-date">{String(s.UPDATED_AT).slice(0, 16).replace('T', ' ')}</span>
                                <button className="saved-del" onClick={(e) => deleteSchedule(s.ID, e)}>삭제</button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* 입력 폼 */}
            <div className="schedule-form">
                <div
                    className={`schedule-drop ${file ? 'has-file' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                    {file
                        ? <span>📄 {file.name}</span>
                        : <span>견적서 파일(PDF / 이미지)을 클릭하거나 끌어다 놓으세요</span>}
                </div>

                <textarea
                    className="schedule-text"
                    placeholder="공사일정 요구사항을 입력하세요. 예) 착공 7월 1일, 8월 말까지 마감. 30평 주택 리모델링, 주말 작업 제외."
                    value={scheduleText}
                    onChange={(e) => setScheduleText(e.target.value)}
                    rows={4}
                />

                {error && <div className="schedule-error">{error}</div>}

                <button className="schedule-submit" onClick={handleGenerate} disabled={loading}>
                    {loading ? '일정표 분석 중…' : '일정표 생성'}
                </button>
            </div>

            {/* 결과: 편집 가능 + 간트차트 */}
            {result && (
                <div className="schedule-result">
                    <div className="schedule-result-head">
                        <input
                            className="schedule-title-input"
                            value={result.projectName || ''}
                            onChange={(e) => updateProject(e.target.value)}
                            placeholder="프로젝트명"
                        />
                        <div className="schedule-actions">
                            {savedMsg && <span className="schedule-saved-msg">{savedMsg}</span>}
                            <button className="schedule-save" onClick={saveSchedule} disabled={saving}>
                                {saving ? '저장 중…' : (currentId ? '수정 저장' : '저장')}
                            </button>
                        </div>
                    </div>

                    {/* 간트차트 */}
                    {gantt && (
                        <>
                            <div className="schedule-range">{fmt(gantt.min)} ~ {fmt(gantt.max)} · 총 {gantt.totalDays}일</div>
                            <div className="schedule-legend">
                                {gantt.phases.filter(Boolean).map(p => (
                                    <span key={p} className="legend-item">
                                        <i style={{ background: gantt.colorOf(p) }} /> {p}
                                    </span>
                                ))}
                            </div>
                            <div className="gantt">
                                {gantt.rows.map(t => (
                                    <div className="gantt-row" key={t.id}>
                                        <div className="gantt-label">
                                            <strong>{t.name}</strong>
                                            <em>{t.startDate} ~ {t.endDate} ({t.durationDays}일)</em>
                                        </div>
                                        <div className="gantt-track">
                                            {!t.invalid && (
                                                <div
                                                    className="gantt-bar"
                                                    style={{ left: `${t.left}%`, width: `${t.width}%`, background: t.color }}
                                                    title={`${t.name} · ${t.phase}`}
                                                >
                                                    <span className="gantt-bar-text">{t.durationDays}일</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* 편집 표 */}
                    <table className="schedule-table">
                        <thead>
                            <tr><th>작업</th><th>단계</th><th>시작</th><th>종료</th><th>기간</th><th>선행(id)</th><th></th></tr>
                        </thead>
                        <tbody>
                            {result.tasks.map((t, idx) => (
                                <tr key={t.id}>
                                    <td><input value={t.name} onChange={(e) => updateTask(idx, 'name', e.target.value)} /></td>
                                    <td><input className="col-phase" value={t.phase || ''} onChange={(e) => updateTask(idx, 'phase', e.target.value)} /></td>
                                    <td><input type="date" value={t.startDate} onChange={(e) => updateTask(idx, 'startDate', e.target.value)} /></td>
                                    <td><input type="date" value={t.endDate} onChange={(e) => updateTask(idx, 'endDate', e.target.value)} /></td>
                                    <td className="col-days">{t.durationDays}일</td>
                                    <td><input className="col-deps" value={(t.dependsOn || []).join(', ')} onChange={(e) => updateDeps(idx, e.target.value)} placeholder="t1, t2" /></td>
                                    <td><button className="row-del" onClick={() => removeTask(idx)}>×</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button className="schedule-addrow" onClick={addTask}>+ 작업 추가</button>

                    <p className="schedule-hint">표의 값을 직접 수정한 뒤 <b>저장</b>을 누르세요. 시작·종료일을 바꾸면 기간과 간트차트가 자동으로 갱신됩니다. 선행 작업은 다른 작업의 id를 쉼표로 입력합니다.</p>
                </div>
            )}
        </div>
    );
};

export default Schedule;